// react core
import { Fragment, useContext, useState, useEffect, useCallback } from "react";

// material design
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";

// fonts
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQrcode as iconActiveOffers } from "@fortawesome/pro-duotone-svg-icons";
// import { faHandPointer as iconPointer } from "@fortawesome/pro-thin-svg-icons";
// import { faPersonWalkingArrowLoopLeft as iconBack } from "@fortawesome/pro-solid-svg-icons";
import { faClose as iconClose } from "@fortawesome/pro-thin-svg-icons";

// entzy resources
import configEntzy from "components/config/ConfigEntzy";
import InputForm from "components/input/InputForm";
import { TickingViewer } from "components/event/lists/ListTickets";

// entzy context and services
import { MainContext } from "components/main/MainContext";
import { EventContext } from "components/event/EventContext";
import { ActionAlert } from "components/utils/common/CommonLoaders";
import { ActionButton } from "components/utils/common/CommonButtons";
import { TextTag } from "components/utils/common/CommonTags";
import {
  serviceCookieGet,
  serviceCookieSet,
  serviceCookieClear,
} from "services/graphql/call";

function ActionDatesRider(props) {
  const user = props.user;
  const userReload = props.userReload;
  const navigate = props.navigate;
  const handleCloseAction = props.handleCloseAction;
  const viewNewOfferOnly = props.viewNewOfferOnly;
  const mainContext = useContext(MainContext);
  const eventContext = useContext(EventContext);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [actionReload, setActionReload] = useState("none");
  const [selectionState, setSelectionState] = useState(null);
  const [newOffer, setNewOffer] = useState(viewNewOfferOnly || false);
  const [offerSuccess, setOfferSuccess] = useState(false);
  const [offerAlert, setOfferAlert] = useState(false);
  const [offerMessage, setOfferMessage] = useState("");
  const [visibleTicketId, setVisibleTicketId] = useState(null);

  const anchorTop = "anchor-action-dates-rider";
  const anchorTicketsContainer = "anchor-tickets-offered";

  const steps = [
    "Select offer type",
    "Set your group size",
    "Tick possible dates",
  ];

  // cookie constants
  const selectionStateCookieName = "payment_selection_state";

  const handleNewOffer = () => {
    setNewOffer(!newOffer);
  };

  // on offer completion redirect to calendar
  // the full page reload will also reload user when required
  const handleConfirmation = () => {
    setTimeout(() => {
      // handleCloseAction();
      // userReload();
      window.location.href =
        configEntzy.URL_POINTERS.MAIN +
        eventContext.state.event.data.Url +
        "/calendar";
    }, 2000);
  };

  const handlePinEvent = async () => {
    if (!eventContext.state.event.pinned) {
      const response = await eventContext.preparePinEvent({
        EventId: eventContext.state.event.data.EventId,
        pinned: true,
      });
      if (response.alert) {
        mainContext.updateAlert(response);
      } else {
        eventContext.pinEvent(response);
        mainContext.addEvent(
          eventContext.state.event.data,
          eventContext.state.event.owner,
          eventContext.state.event.manager
        );
      }
    }
  };
  const cbHandlePinEvent = useCallback(handlePinEvent, [
    eventContext,
    mainContext,
  ]);

  const handlePaymentFormCancel = async () => {
    setShowPaymentForm(false);
    setActionReload("payments-" + Date.now());
  };

  const placeOffer = async (offer) => {
    const ticket = await eventContext.prepareTicketOffer({
      EventId: eventContext.state.event.data.EventId,
      TypeId: offer.ticketTypeId,
      Quantity: offer.ticketQuantity,
      DateList: offer.ticketDateList,
      Holdings: offer.ticketHoldings,
    });
    if (ticket.alert) {
      return {
        alert: true,
        message: ticket.message,
      };
    } else {
      eventContext.updateViewerTickets(ticket);
      eventContext.updateEventStamp({
        key: "Dates",
      });
      setOfferSuccess(true);
      // move top and reset
      const element = document.getElementById(anchorTop);
      if (element) {
        eventContext.state.toolbox.scrollIntoViewIfNeeded(element);
      }
      eventContext.setCallbackLoader(true);
      setNewOffer(false);
      setActionReload("done-" + Date.now());
      // pin event if not already pinned
      await cbHandlePinEvent();
      return { alert: false };
    }
  };
  const cbPlaceOffer = useCallback(placeOffer, [
    eventContext,
    cbHandlePinEvent,
  ]);

  const handleActionCallback = async (data) => {
    setOfferSuccess(false);
    const selection = {
      typeData: data.filter((obj) => obj.id === "ticket-type")[0].value,
      groupData: data.filter((obj) => obj.id === "ticket-group")[0].value,
      // membersData: data.filter((obj) => obj.id === "ticket-members")[0].value,
      // datesData: data.filter((obj) => obj.id === "ticket-dates")[0].value,
    };
    selection.membersData = {
      value: {
        named: [],
        sharedPayment: false,
        totals: {
          group: selection.groupData.value,
          named: 0,
          unnamed: selection.groupData.value,
        },
      },
    };
    selection.datesData = [];
    // build new ticket offer with dates
    if (!eventContext.state.event.manager) {
      if (selection.typeData && selection.groupData) {
        const offer = {
          ticketTypeId: selection.typeData.value,
          ticketQuantity: selection.groupData.value,
          ticketDateList: selection.datesData,
          ticketHoldings: selection.membersData.value,
        };
        if (
          !eventContext.state.user.status.payment_set &&
          selection.typeData.price > 0
        ) {
          offer.paymentUpdate = true;
          setSelectionState(offer);
          serviceCookieSet(selectionStateCookieName, JSON.stringify(offer));
          setShowPaymentForm(true);
          return { alert: false };
        } else {
          const offerResponse = await placeOffer(offer);
          if (!offerResponse.alert) {
            handleConfirmation();
          }
          return offerResponse;
        }
      } else {
        return {
          alert: true,
          message: "Offer type selection and group size are required",
        };
      }
    } else {
      return {
        alert: true,
        message: "Event rider permissions required to place offers",
      };
    }
  };

  const handlePaymentActionCallback = async (data) => {
    const paymentSettings = data.filter(
      (obj) => obj.id === "payment-primary"
    )[0].value;
    if (
      paymentSettings &&
      paymentSettings.paymentId &&
      paymentSettings.paymentId.length
    ) {
      const response = await eventContext.prepareUpdatePaymentMethod({
        key: "PaymentId",
        value: paymentSettings.paymentId,
      });
      if (response.alert) {
        return response;
      } else {
        const offerResponse = await placeOffer(selectionState);
        serviceCookieClear(
          configEntzy.STRIPE_SETUP_INTENT_PARAMS.cookie_payment_set
        );
        serviceCookieClear(selectionStateCookieName);
        setSelectionState(null);
        handleConfirmation();
        return offerResponse;
      }
    } else {
      return {
        alert: true,
        message: "A valid payment method required",
      };
    }
  };

  // effect to check if payment cookie is set to trigger place offer
  useEffect(() => {
    const handlePaymentSetCookieCallback = async () => {
      const paymentCookie = serviceCookieGet(
        configEntzy.STRIPE_SETUP_INTENT_PARAMS.cookie_payment_set
      );
      const paymentSelectionState = serviceCookieGet(selectionStateCookieName);
      if (paymentCookie && paymentSelectionState) {
        serviceCookieClear(
          configEntzy.STRIPE_SETUP_INTENT_PARAMS.cookie_payment_set
        );
        serviceCookieClear(selectionStateCookieName);
        const offerResponse = await cbPlaceOffer(
          JSON.parse(paymentSelectionState)
        );
        if (offerResponse.alert) {
          setOfferAlert(true);
          setOfferMessage(offerResponse.message);
        }
        setTimeout(() => {
          handleCloseAction();
          navigate(
            configEntzy.URL_POINTERS.MAIN + eventContext.state.event.data.Url
          );
        }, 5000);
      }
    };
    handlePaymentSetCookieCallback();
  }, [
    cbPlaceOffer,
    handleCloseAction,
    navigate,
    userReload,
    eventContext.state.event.data.Url,
  ]);

  return (
    <Box id={anchorTop} className="box-default">
      <Box
        className="box-default"
        sx={{
          pt: configEntzy.APP_SPACING_LG,
          pb: configEntzy.APP_SPACING_LG,
        }}
        hidden={!viewNewOfferOnly}
      >
        <Stepper alternativeLabel>
          {steps.map((label) => (
            <Step key={label} active={true}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
      <Box
        className="box-default"
        sx={{
          pt: configEntzy.APP_SPACING_LG,
          pb: configEntzy.APP_SPACING_LG,
        }}
        hidden={true}
      >
        <ActionButton
          text={newOffer ? "Cancel" : "New Offer"}
          color={newOffer ? "bright" : "dusk"}
          onClick={handleNewOffer}
          startIcon={
            <FontAwesomeIcon
              icon={newOffer ? iconClose : iconActiveOffers}
              transform="right-4"
              fixedWidth
            />
          }
        />
      </Box>

      <Box className="box-default" hidden={!newOffer}>
        <Box className="box-default">
          <Container maxWidth="md" disableGutters>
            <Box
              className="box-default"
              sx={{
                pb: configEntzy.APP_SPACING_LG2X,
              }}
            >
              <Box className="box-default" hidden={showPaymentForm}>
                <InputForm
                  user={user}
                  key={actionReload}
                  navigate={props.navigate}
                  fields={[
                    {
                      id: "ticket-type",
                      type: "selector-basic",
                      label: "Select offer type",
                      value: null,
                      emptyValue: "",
                      required: true,
                      selectPrompt: true,
                      disabled: !eventContext.state.user.connected,
                      options:
                        eventContext.state.event.calendar.tickets.selectable
                          .active,
                    },
                    {
                      id: "ticket-group",
                      type: "selector-basic",
                      label: "Select how many people on this offer",
                      value: null,
                      emptyValue: "",
                      required: true,
                      hidden: true,
                      selectPrompt: true,
                      dependsOn: {
                        selection: "ticket-type",
                        property: "groupSelectable",
                        // submitHidden: true,
                        submitHidden: false,
                      },
                      disabled: !eventContext.state.user.connected,
                      options:
                        eventContext.state.event.calendar.tickets.groups
                          .selectable,
                    },
                    // {
                    //   id: "ticket-members",
                    //   type: "picker-member-number-rocker",
                    //   label: "Ticket Holders",
                    //   placeholder: "Your holding must be at least 1",
                    //   value: [],
                    //   required: true,
                    //   multiple: true,
                    //   hidden: true,
                    //   dependsOn: {
                    //     selection: "ticket-group",
                    //     submitHidden: true,
                    //   },
                    //   disabled: !eventContext.state.user.connected,
                    //   options: mainContext.state.interaction.users.filter(
                    //     (iuser) =>
                    //       iuser.identity !== null &&
                    //       iuser.identity !== user.identity
                    //   ),
                    // },
                    // {
                    //   id: "ticket-dates",
                    //   type: "picker-calendar",
                    //   label: "Place Some Date offers (optional)",
                    //   placeholder:
                    //     "Select any initial dates to offer this ticket",
                    //   value: [],
                    //   required: true,
                    //   multiple: true,
                    //   hidden: true,
                    //   dependsOn: {
                    //     selection: "ticket-members",
                    //     submitHidden: false,
                    //   },
                    //   disabled: !eventContext.state.user.connected,
                    //   options:
                    //     eventContext.state.event.calendar.dates.selectable,
                    //   optionsDecorate: {
                    //     ticking:
                    //       eventContext.state.event.calendar.dates.combined
                    //         .active,
                    //     launched:
                    //       eventContext.state.event.calendar.dates.launched.all
                    //         .keys,
                    //   },
                    // },
                  ]}
                  submitText="Continue to your Offers"
                  submitType="flow"
                  disabledSubmit={!eventContext.state.user.connected}
                  disabledTitle="Connect to continue"
                  disabledText="Tap here to activate date offering. A connection is required to save the offer details."
                  disabledClick={eventContext.toggleConnect}
                  // changeEmitter={handleActionEmitter}
                  callback={handleActionCallback}
                />
              </Box>
              {/* PAYMENT FORM */}
              <Box
                className="box-default"
                hidden={!showPaymentForm}
                sx={{
                  pt: configEntzy.APP_SPACING_MD,
                }}
              >
                <Box
                  className="box-default"
                  hidden={eventContext.state.user.status.payment_set}
                  sx={{
                    p: configEntzy.APP_SPACING_LG,
                  }}
                >
                  <Typography variant="subtitle2">PAYMENT DETAILS</Typography>
                  <Typography
                    variant="subtitle2"
                    color="primary"
                    sx={{
                      mt: configEntzy.APP_SPACING_MD,
                    }}
                  >
                    Add billing information for chargeable offer selections. You
                    will only be charged if one of your offers triggers with an
                    event launch
                  </Typography>
                </Box>
                <Box
                  className="box-default"
                  sx={{
                    pb: configEntzy.APP_SPACING_LG,
                  }}
                >
                  <InputForm
                    user={user}
                    userReload={userReload}
                    navigate={props.navigate}
                    page={props.page}
                    redirectPage={props.redirectPage}
                    searchParams={props.searchParams}
                    fields={[
                      {
                        id: "payment-primary",
                        type: "payment-method",
                        label: "Primary payment method",
                        value: "",
                        required: true,
                      },
                    ]}
                    submitType="auto"
                    submitText="Add Payment Details"
                    callback={handlePaymentActionCallback}
                    cancel={handlePaymentFormCancel}
                    cancelText="Cancel"
                    confirmation="Payment details added"
                  />
                </Box>
              </Box>
            </Box>
            <Box className="box-default" hidden={showPaymentForm}>
              <Container maxWidth="xs">
                <Typography
                  variant="subtitle2"
                  color="primary"
                  sx={{
                    mt: configEntzy.APP_SPACING_SM,
                  }}
                >
                  IMPORTANT! Create your offers with all of your group on the
                  same offer type. This will ensures you all launch on the same
                  date.
                </Typography>
              </Container>
            </Box>
          </Container>
        </Box>
      </Box>

      <Box className="box-default" hidden={newOffer}>
        {/* <Box
          className="box-default"
          sx={{
            p: configEntzy.APP_SPACING_MD,
            color: "white",
          }}
        >
          <Typography variant="subtitle2">
            <span>Your tickets in play</span>
            <span>&nbsp;&nbsp;&nbsp;&nbsp;</span>
            <FontAwesomeIcon icon={iconActiveOffers} transform="shrink-4" />
            <span>&nbsp;</span>
            <span>{eventContext.state.viewer.tickets.offered.length}</span>
          </Typography>
          {eventContext.state.viewer.tickets.offered.length === 0 ? (
            <Fragment></Fragment>
          ) : (
            <Fragment>
              <Typography
                variant="subtitle1"
                color="tertiary"
                sx={{
                  mt: configEntzy.APP_SPACING_MD,
                }}
                hidden={eventContext.state.viewer.tickets.offered.length === 0}
              >
                <em>Edit or revoke offers any time before launch.</em>
              </Typography>
              <Typography
                variant="subtitle1"
                color="tertiary"
                hidden={eventContext.state.viewer.tickets.offered.length === 0}
              >
                <em>Successful offers will move to eventuated.</em>
              </Typography>
            </Fragment>
          )}
        </Box> */}

        {user.connected ? (
          <Box
            className="box-default nooverflow"
            sx={{
              pt: configEntzy.APP_SPACING_MD,
            }}
          >
            <Container maxWidth="md">
              {offerSuccess && (
                <Box
                  className="box-default"
                  sx={{
                    pb: configEntzy.APP_SPACING_LG,
                  }}
                >
                  <Box className="box-default">
                    <ActionAlert
                      severity="success"
                      title="Offer Created"
                      message="Offer in play and can be managed below. If it eventuates you'll be notified and it will move from here to your matched dates."
                      onClose={() => setOfferSuccess(false)}
                    />
                  </Box>
                </Box>
              )}
              {offerAlert && (
                <Box
                  className="box-default"
                  sx={{
                    pb: configEntzy.APP_SPACING_LG,
                  }}
                >
                  <ActionAlert
                    severity="error"
                    title="Offer Could Not Be Placed"
                    message={offerMessage}
                    onClose={() => setOfferAlert(false)}
                  />
                </Box>
              )}
            </Container>
            {eventContext.state.viewer.tickets.offered.length > 0 && (
              <Box
                className="box-default"
                sx={{
                  pt: configEntzy.APP_SPACING_MD,
                  pb: configEntzy.APP_SPACING_MD2X,
                }}
              >
                <Box
                  // key={eventContext.state.id}
                  id={anchorTicketsContainer}
                  className="box-default custom-scrollbar horizontal"
                  sx={{
                    // height: configEntzy.AVATAR_CONTAINER_XL,
                    pb: configEntzy.APP_SPACING_MD,
                    overflowY: "hidden",
                    overflowX: "scroll",
                    display: "flex",
                    flexDirection: "row",
                    flexWrap: "nowrap",
                    whiteSpace: "nowrap",
                    alignItems: "center",
                  }}
                  onScroll={(e) => {
                    const container = e.currentTarget;
                    const scrollLeft = container.scrollLeft;
                    const ticketCount =
                      eventContext.state.viewer.tickets.offered.length;
                    const ticketWidth = container.scrollWidth / ticketCount;
                    const ticketScroll = scrollLeft / ticketWidth;
                    const ticketId =
                      eventContext.state.viewer.tickets.offered[
                        Math.round(ticketScroll)
                      ].TicketId;
                    setVisibleTicketId(ticketId);
                  }}
                >
                  <Fragment>
                    {eventContext.state.viewer.tickets.offered.map(
                      (ticket, index) => {
                        return (
                          <Box
                            id={"anchor-ticket-" + ticket.TicketId}
                            key={ticket.TicketId}
                            className="box-default"
                            sx={{
                              flex: "0 0 auto",
                              pr: configEntzy.APP_SPACING_SM2X,
                              pl: configEntzy.APP_SPACING_SM2X,
                            }}
                          >
                            <Box
                              className="box-default"
                              sx={{
                                mb: configEntzy.APP_SPACING_MD,
                              }}
                            >
                              <Typography variant="subtitle2">
                                <Chip
                                  label={
                                    <span>
                                      <span style={{ color: "white" }}>
                                        Active Offer {index + 1} of{" "}
                                        {
                                          eventContext.state.viewer.tickets
                                            .offered.length
                                        }
                                      </span>
                                    </span>
                                  }
                                  sx={{
                                    fontSize: configEntzy.FONT_SCALE_LG,
                                    fontWeight: "bold",
                                  }}
                                />
                              </Typography>
                            </Box>
                            <TickingViewer
                              user={eventContext.state.user}
                              mainContext={mainContext}
                              eventContext={eventContext}
                              ticket={ticket}
                              anchorTop={anchorTop}
                              scrollStart={() => {
                                const container = document.getElementById(
                                  anchorTicketsContainer
                                );
                                if (container) {
                                  container.scrollTo({
                                    left: 0,
                                    behavior: "smooth",
                                  });
                                }
                                setVisibleTicketId(ticket.TicketId);
                              }}
                            />
                          </Box>
                        );
                      }
                    )}
                  </Fragment>
                </Box>
                {eventContext.state.viewer.tickets.offered.length > 1 && (
                  <Box
                    className="box-default"
                    sx={{ pt: configEntzy.APP_SPACING_MD }}
                  >
                    {eventContext.state.viewer.tickets.offered.map(
                      (ticket, index) => (
                        <TextTag
                          key={ticket.TicketId}
                          text={
                            <span>
                              <span>&nbsp;</span>
                              {index + 1}
                              <span>&nbsp;</span>
                            </span>
                          }
                          size="lg"
                          rounded={true}
                          bgColor={
                            visibleTicketId === ticket.TicketId
                              ? "purple"
                              : "black"
                          }
                          color="white"
                          onClick={() => {
                            const container = document.getElementById(
                              "anchor-tickets-offered"
                            );
                            const target = document.getElementById(
                              "anchor-ticket-" + ticket.TicketId
                            );
                            if (container && target) {
                              container.scrollTo({
                                left: target.offsetLeft - container.offsetLeft,
                                behavior: "smooth",
                              });
                              // setVisibleTicketId(ticket.TicketId);
                            }
                          }}
                        />
                      )
                    )}
                  </Box>
                )}
                <Box
                  className="box-default"
                  sx={{ pt: configEntzy.APP_SPACING_MD }}
                >
                  <Typography variant="subtitle1">
                    Offers that launch move to{" "}
                    <span
                      className="action-pointer"
                      style={{ color: "orange" }}
                      onClick={() => {
                        eventContext.focusModule("entry");
                      }}
                    >
                      eventuated
                    </span>
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        ) : (
          <Box
            className="box-default"
            sx={{
              pt: configEntzy.APP_SPACING_LG,
              pb: configEntzy.APP_SPACING_LG3X,
            }}
          >
            <Typography variant="subtitle2">
              Connect to place date offers
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default ActionDatesRider;
